import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { AnchorProps } from "@headlessui/react/dist/internal/floating";
import classNames from "classnames";
import clsx from "clsx";
import { debounce } from "lodash";
import { Fragment, ReactNode, useEffect, useRef, useState } from "react";
import { Icon } from "../icon/icon";

const handleSearch = (searchFunction: (val: any) => void, query: any) => {
  searchFunction(query);
};

const handleDebouncedSearch = debounce(handleSearch, 800);

const Dropdown = ({
  children,
  menuItems,
  customMenuItems = undefined,
  className = "",
  menuClassName = "",
  menuItemsClassName = "",
  position = "bottom",
  closeOnClick = true,
  searchable = false,
  onSearch = () => {},
  onMenuClose = () => {},
  menuExactWidth = false,
}: {
  children: ReactNode;
  menuItems?: {
    name: ReactNode;
    value?: string;
    onClick?: (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  }[];
  customMenuItems?: ReactNode;
  className?: string;
  menuClassName?: string;
  menuItemsClassName?: string;
  searchable?: boolean;
  onSearch?: (val: string) => void;
  onMenuClose?: () => void;
  menuExactWidth?: boolean;

  position?: AnchorProps;
  closeOnClick?: boolean;
}) => {
  const [width, setWidth] = useState(0);
  const [searchParams, setSearchParams] = useState<string>("");

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (containerRef.current) {
      setWidth(containerRef.current.offsetWidth);
    }
  }, [containerRef?.current?.offsetWidth]);

  useEffect(() => {
    handleDebouncedSearch(onSearch, searchParams);
    // eslint-disable-next-line
  }, [searchParams]);

  return (
    <Menu
      as="div"
      className={`relative inline-block text-left !outline-none focus-within:!outline-none focus:!outline-none ${className}`}
      data-open={true}
    >
      {({ open, close }) => {
        if (!open) {
          setSearchParams("");
          onMenuClose();
        }

        return (
          <>
            <div
              className="w-full outline-none focus-within:outline-none focus:outline-none"
              ref={containerRef}
            >
              <MenuButton as={Fragment} autoFocus={false}>
                {children}
              </MenuButton>
            </div>

            <MenuItems
              className={classNames(
                `relative !z-[900] flex flex-col overflow-x-hidden overflow-y-scroll`,
                {
                  "rounded-md border bg-white": customMenuItems === undefined,
                  "w-full min-w-max": !menuExactWidth,
                },
                "[--anchor-gap:16px]",
                "outline-none focus-within:outline-none focus:outline-none",
                menuClassName,
              )}
              transition
              anchor={position}
              style={{
                width: width,
                minWidth: menuExactWidth ? `${width} !important` : undefined,
                maxWidth: menuExactWidth ? `${width} !important` : undefined,
              }}
              modal
            >
              {searchable && (
                <div className="sticky top-0 flex w-full items-center gap-2 border-b border-grey-100 bg-white p-3">
                  <Icon
                    svg="search"
                    width={20}
                    height={20}
                    className="text-grey-400"
                  />

                  <input
                    placeholder="Search"
                    className="flex-1 text-sm outline-none autofill:bg-transparent focus:outline-none autofill:focus:bg-transparent"
                    onChange={(e) => setSearchParams(e.target.value)}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                    }}
                  />
                </div>
              )}

              {customMenuItems !== undefined ? (
                <MenuItem
                  as={"div"}
                  onClick={(e) => {
                    if (!closeOnClick) {
                      e.preventDefault();
                    }
                  }}
                >
                  {customMenuItems}
                </MenuItem>
              ) : (
                menuItems?.map((item, index) => {
                  const key = `menu-item-${index}`;
                  return (
                    <MenuItem
                      key={key}
                      as="button"
                      type="button"
                      onClick={(e) => {
                        if (!closeOnClick) {
                          e.preventDefault();
                        }
                        if (item.onClick) {
                          item.onClick(e);
                        }
                      }}
                      className={clsx(
                        "w-full border-b bg-inherit p-3 text-left text-sm font-medium text-primary transition-all duration-200 last:border-b-0 hover:bg-grey-50",
                        menuItemsClassName,
                      )}
                    >
                      {item.name}
                    </MenuItem>
                  );
                })
              )}

              {searchable && menuItems && menuItems.length === 0 ? (
                <div className="flex h-full w-full flex-col items-center justify-center gap-[10px]">
                  <Icon
                    svg="search-x"
                    width={44}
                    height={44}
                    className="rounded-full bg-grey-100 p-3 text-grey-600"
                  />

                  <p className="text-sm">No Result Found</p>
                </div>
              ) : undefined}
            </MenuItems>
          </>
        );
      }}
    </Menu>
  );
};

export default Dropdown;
